import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Chip } from '@material-ui/core'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { ASNDetailTable } from 'src/components/Table/detail/ASN'
import { ASNDialog } from 'src/modules/supply_chain/components/ASNDialog'
import { ASNExpectedUnitListTable } from 'src/components/Table/list/ASNExpectedUnit'
import { PalletListTable } from 'src/components/Table/list/Pallet'
import { ASNUnitListTable } from 'src/components/Table/list/ASNUnit'
import { EventListTable } from 'src/components/Table/list/Event'
import { FreightOrderDialog } from 'src/modules/supply_chain/components/freight/OrderDialog'
import { StatsTable } from 'src/components/Stats/StatsTable'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'
import { Tag } from 'src/api/models'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

const InventoryTab = () => {
  const { guid } = useParams<PathProps>()

  const { data: receivingStats } = useQuery(['asn-receiving-stats', guid], () =>
    skuit.sc.asn.receivingStats(guid)
  )
  const expectedPaginator = useQueryPaginator(
    'asn-expected-unit-list',
    skuit.sc.asn.expectedUnits.list,
    { params: { page_size: 5, asn: guid } }
  )
  const receivedPaginator = useQueryPaginator('asn-unit-list', skuit.sc.asn.units.list, {
    params: { page_size: 5, asn: guid },
  })
  const palletsPaginator = useQueryPaginator('pallet-list', skuit.sc.pallets.list, {
    params: { page_size: 5, asns: guid },
  })

  return (
    <DetailTab>
      {receivingStats && (
        <StatsTable
          title="Receiving Summary"
          titles={[
            'Total Pallet Count',
            'Empty Pallet Count',
            'Expected Unit Count',
            'Received Unit Count',
          ]}
          values={[
            receivingStats.total_pallet_count,
            receivingStats.empty_pallet_count,
            receivingStats.total_expected_unit_count,
            receivingStats.total_received_unit_count,
          ]}
        />
      )}
      <ASNExpectedUnitListTable
        paginator={expectedPaginator}
        fields={['productName', 'product', 'sku', 'numUnits', 'receivedStatus']}
      />
      <ASNUnitListTable
        paginator={receivedPaginator}
        title="Received Units"
        fields={['productName', 'product', 'sku', 'numUnits']}
      />
      <PalletListTable
        paginator={palletsPaginator}
        fields={['name', 'clientID', 'skus', 'totalUnits', 'batch', 'path']}
      />
    </DetailTab>
  )
}

const AttachmentsTab = () => {
  const { guid } = useParams<PathProps>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)

  const attachmentsPaginator = useQueryPaginator(
    'attachment-list',
    () => skuit.sc.asn.attachments.list(guid),
    {
      params: { page_size: 4, asn: guid },
    }
  )

  return (
    <DetailTab>
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={() => attachmentsPaginator.refresh()}
          addAttachment={(config) => skuit.sc.asn.attachments.create(guid, config?.data, config)}
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <PaginatedFilePreviewGallery
        title="Attachments"
        paginator={attachmentsPaginator}
        getIdent={(item) => item.guid}
        onAdd={() => {
          setAddAttachmentDialogOpen(true)
        }}
        renderItem={(item) => {
          return (
            <>
              <GenericPreview url={item.file} />
              <div>
                {item.tags.map((tag: Tag) => (
                  <Chip key={tag.slug} label={tag.name} />
                ))}
              </div>
            </>
          )
        }}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="ASN Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const ASNPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)
  const [orderCreateOpen, setOrderCreateOpen] = useState(false)

  const asn = useQuery(['asn-details', guid], () => skuit.sc.asn.retrieve(guid))
  const customFieldValuePaginator = useQueryPaginator(
    'custom-field-values',
    skuit.sc.asn.customFieldValues.list,
    { params: { asn: guid } }
  )
  const customFieldValues: Record<string, string> = {}
  for (const field of customFieldValuePaginator.data.results) {
    customFieldValues[field.name] = field.value
  }

  const refresh = () => {
    asn.refetch()
  }

  return (
    <>
      {createOpen && (
        <ASNDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      {orderCreateOpen && (
        <FreightOrderDialog
          open={orderCreateOpen}
          handleClose={() => {
            setOrderCreateOpen(false)
          }}
          asn={asn.data}
        />
      )}
      <DetailPage
        obj={asn}
        title="Advanced Shipping Notice"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'ASN', url: '/shipping/asn' },
        ]}
        detailComponent={
          <ASNDetailTable obj={asn} refresh={refresh} customFields={customFieldValues} />
        }
        secondaryComponent={
          <Workflow
            title="Order Workflow"
            loading={asn.isFetching}
            workflow={asn.data?.workflow || []}
            status={asn.data?.workflowStatus || ''}
            actions={asn.data?.getWorkflowActions(() => asn.refetch()) || {}}
          />
        }
        menuItems={[
          {
            Icon: AddIcon,
            actions: [
              { title: 'New ASN', onClick: () => setCreateOpen(true) },
              { title: 'Create Order from ASN', onClick: () => setOrderCreateOpen(true) },
            ],
          },
        ]}
        tabs={['Inventory', 'Attachments', 'Events']}
      >
        <InventoryTab />
        <AttachmentsTab />
        <EventsTab />
      </DetailPage>
    </>
  )
}
